import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { LoginRequestSchema, type LoginRequest } from '@seo/types';
import { Button } from '@/components/ui/button';
import { Surface } from '@/components/ui/surface';
import { PasswordField, TextField } from '@/components/ui/form-field';
import { Spinner } from '@/components/spinner';
import { fadeUp, stagger } from '@/components/motion';
import { useLogin } from '@/features/auth/use-login';
import { AuthLayout } from '@/routes/_auth';
import { humanizeApiError } from '@/lib/error-messages';
import { ApiError } from '@/lib/api';
import { useStaticTitle } from '@/lib/use-crumbs';

export function LoginRoute(): JSX.Element {
  const navigate = useNavigate();
  // Sanitized by the route's validateSearch — always an internal path.
  const { next } = useSearch({ strict: false }) as { next?: string };
  const login = useLogin();
  useStaticTitle('Sign in');
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginRequest>({
    resolver: zodResolver(LoginRequestSchema),
    defaultValues: { email: '', password: '' },
  });

  return (
    <AuthLayout>
      <Surface tone="raised" className="w-full">
        <motion.form
          variants={stagger}
          initial="hidden"
          animate="show"
          className="space-y-5 p-7 sm:p-8"
          onSubmit={handleSubmit(async (values) => {
            try {
              await login.mutateAsync(values);
              // Return to the page the user was heading for before the
              // login wall — falling back to the dashboard.
              await navigate({ to: (next ?? '/') as string });
            } catch (e) {
              const { title, description } = formatLoginError(e);
              toast.error(title, { description });
            }
          })}
          noValidate
        >
          <motion.div variants={fadeUp} className="space-y-1.5">
            <h1 className="text-2xl font-semibold tracking-tight">
              Welcome back
            </h1>
            <p className="text-sm text-muted-foreground">
              Sign in to pick up where you left off.
            </p>
          </motion.div>

          <motion.div variants={fadeUp}>
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              autoFocus
              placeholder="you@company.com"
              error={errors.email?.message}
              {...register('email')}
            />
          </motion.div>

          <motion.div variants={fadeUp}>
            <PasswordField
              label="Password"
              autoComplete="current-password"
              placeholder="••••••••••"
              error={errors.password?.message}
              {...register('password')}
            />
          </motion.div>

          <motion.div variants={fadeUp}>
            <Button
              type="submit"
              size="lg"
              className="w-full bg-gradient-primary text-primary-foreground hover:opacity-95"
              disabled={login.isPending || isSubmitting}
            >
              {login.isPending ? <Spinner className="mr-2" /> : null}
              Sign in
              {!login.isPending ? <ArrowRight className="ml-1 h-4 w-4" /> : null}
            </Button>
          </motion.div>

          <motion.p
            variants={fadeUp}
            className="text-center text-sm text-muted-foreground"
          >
            New here?{' '}
            <Link
              to="/signup"
              search={next ? { next } : {}}
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              Create an account
            </Link>
          </motion.p>
        </motion.form>
      </Surface>
    </AuthLayout>
  );
}

/**
 * Sign-in specific error copy. Wrong-password (401 without a session refresh
 * chance because /v1/auth/login is `unauthenticated`) becomes the friendly
 * "check your email + password" message; everything else delegates to the
 * shared humanizer.
 */
function formatLoginError(err: unknown): { title: string; description: string } {
  if (err instanceof ApiError && err.status === 401) {
    return {
      title: 'Sign in failed',
      description: 'Check your email and password, then try again.',
    };
  }
  const { title, description } = humanizeApiError(err);
  return {
    title: title === 'Something went wrong' ? 'Sign in failed' : title,
    description,
  };
}
